import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { fundPath, usePlatform, type FundIndex } from "@/platform/data";
import { Monogram, companyPath } from "@/platform/ui";
import { FUNCTIONS, FUNCTION_ID } from "@/platform/functions";

export type CompanyRow = [symbol: string, name: string, exchange: string, industry: string, act30: number, index?: string | null, division?: string | null, cik?: number | null, currency?: string | null];
export const useCompanies = () => usePlatform<{ built: string; companies: CompanyRow[] }>("companies.json");

/** Ticker first, then the start of a name, then anywhere in the name. */
export const rank = (list: CompanyRow[], q: string, n = 8) => {
  const s = q.trim().toLowerCase();
  if (!s) return [];
  const score = (c: CompanyRow) => {
    const t = c[0].toLowerCase(), nm = c[1].toLowerCase();
    return t === s ? 0 : t.startsWith(s) ? 1 : nm.startsWith(s) ? 2 : nm.split(/\W+/).some((w) => w.startsWith(s)) ? 3 : nm.includes(s) ? 4 : 9;
  };
  return list.map((c) => [score(c), c] as const).filter(([sc]) => sc < 9).sort((a, b) => a[0] - b[0] || b[1][4] - a[1][4]).slice(0, n).map(([, c]) => c);
};

type Fund = FundIndex["funds"][number];
/** Funds by name: the start of the name, then the start of a word, then anywhere; the largest first within each. */
export const rankFunds = (list: Fund[], q: string, n = 4) => {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return [];
  const score = (f: Fund) => {
    const nm = f[1].toLowerCase();
    return nm.startsWith(s) ? 0 : nm.split(/\W+/).some((w) => w.startsWith(s)) ? 1 : nm.includes(s) ? 2 : 9;
  };
  return list.map((f, k) => [score(f), k, f] as const).filter(([sc]) => sc < 9).sort((a, b) => a[0] - b[0] || a[1] - b[1]).slice(0, n).map(([, , f]) => f);
};
const usd = (v: number) => (v >= 1e12 ? `$${(v / 1e12).toFixed(1)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

/** The search box: type a ticker or a name, Enter opens the company's record; a fund's name opens its 13F page
 * (2 Nov 2026). The fund list loads only once something is typed, so the page itself stays light. */
export const Finder = ({ big = false, autoFocus = false }: { big?: boolean; autoFocus?: boolean }) => {
  const all = useCompanies();
  const [q, setQ] = useState("");
  const [i, setI] = useState(0);
  const nav = useNavigate();
  const box = useRef<HTMLInputElement>(null);
  // "AAPL FA": a ticker, then a function code
  const fn = useMemo(() => {
    const m = q.trim().toUpperCase().match(/^([A-Z][A-Z0-9.\-]{0,6})\s+([A-Z0-9]{2,4})$/);
    if (!m || !all || !FUNCTION_ID[m[2]]) return null;
    const c = all.companies.find((x) => x[0] === m[1]);
    return c ? { c, code: m[2], id: FUNCTION_ID[m[2]], label: FUNCTIONS.find((f) => f[0] === m[2])![1] } : null;
  }, [all, q]);
  const funds = usePlatform<FundIndex>(q.trim().length >= 2 ? "funds/index.json" : null);
  const cos = useMemo(() => (all && !fn ? rank(all.companies, q, 6) : []), [all, q, fn]);
  const fds = useMemo(() => (funds && !fn ? rankFunds(funds.funds, q, cos.length >= 4 ? 3 : 5) : []), [funds, q, fn, cos.length]);
  const hits = [...cos.map((c) => ({ key: c[0], to: companyPath(c[0]), c })), ...fds.map((f) => ({ key: f[2], to: fundPath(f[2]), f }))];
  useEffect(() => setI(0), [q]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); box.current?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const go = (h?: { to: string }) => h && nav(h.to);
  return (
    <div className="relative">
      <div className={cn("flex items-center gap-3 rounded-[10px] border border-[var(--sn-border-strong)] bg-[hsl(var(--card))] px-4 focus-within:border-primary", big ? "h-[56px]" : "h-[44px]")}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-muted-foreground"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        <input
          ref={box}
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setI(Math.min(i + 1, hits.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setI(Math.max(i - 1, 0)); }
            if (e.key === "Enter") { if (fn) nav(`${companyPath(fn.c[0])}#${fn.id}`); else go(hits[i]); }
          }}
          placeholder={all ? `Search ${all.companies.length.toLocaleString("en-US")} companies and every 13F fund, or a ticker and function (AAPL FA)` : "Search companies by ticker or name, and funds by name"}
          aria-label="Search companies and funds"
          className={cn("min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground", big ? "text-[17px]" : "text-[14.5px]")}
        />
        <kbd className="hidden rounded border border-[var(--sn-border)] px-1.5 font-mono text-[11px] text-muted-foreground sm:inline">/</kbd>
      </div>
      {fn && (
        <button type="button" onClick={() => nav(`${companyPath(fn.c[0])}#${fn.id}`)} className="absolute inset-x-0 top-[calc(100%+6px)] z-30 flex items-center justify-between rounded-[3px] border border-[var(--sn-border)] bg-white px-4 py-2.5 text-left shadow-[0_8px_24px_-12px_rgba(11,18,32,0.25)]">
          <span className="text-[13.5px] text-foreground"><span className="font-mono font-semibold">{fn.c[0]} {fn.code}</span> <span className="text-muted-foreground">·</span> {fn.label}, {fn.c[1]}</span>
          <kbd className="rounded border border-[var(--sn-border)] px-1.5 font-mono text-[11px] text-muted-foreground">Enter</kbd>
        </button>
      )}
      {hits.length > 0 && (
        <ul className="absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-[10px] border border-[var(--sn-border)] bg-[hsl(var(--card))] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)]">
          {hits.map((h, j) => (
            <li key={h.key}>
              {"f" in h && h.f && (j === 0 || !("f" in hits[j - 1])) && <p className="border-t border-[var(--sn-border)] px-4 pb-1 pt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground first:border-t-0">13F funds</p>}
              <button type="button" onMouseEnter={() => setI(j)} onClick={() => go(h)} className={cn("flex w-full items-center gap-3 px-4 py-2.5 text-left", j === i && "bg-[color-mix(in_srgb,var(--sn-accent)_7%,transparent)]")}>
                {"c" in h && h.c ? (
                  <>
                    <Monogram symbol={h.c[0]} name={h.c[1]} size={26} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-medium text-foreground">{h.c[1]}</span>
                      <span className="block truncate font-mono text-[11px] text-muted-foreground">{h.c[0]} · {h.c[2]} · {h.c[3]}</span>
                    </span>
                  </>
                ) : "f" in h && h.f ? (
                  <>
                    <Monogram symbol={h.f[1].slice(0, 2).toUpperCase()} name={h.f[1]} size={26} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-medium text-foreground">{h.f[1]}</span>
                      <span className="block truncate font-mono text-[11px] text-muted-foreground">13F · {h.f[6].toLocaleString("en-US")} {h.f[6] === 1 ? "position" : "positions"} · {usd(h.f[3])}</span>
                    </span>
                  </>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
